import { useMemo, useState, useCallback } from "react";
import type { AccordionProps } from "./types";
import { AccordionContext } from "./AccordionContext";
import { AccordionItem } from "./AccordionItem";
import { AccordionTrigger } from "./AccordionTrigger";
import { AccordionContent } from "./AccordionContent";
import { cx } from "@/utils/cx";
import styles from "./Accordion.module.css";

/**
 * Normalize value to array format for internal handling
 */
function normalizeValue(
  value: string | string[] | undefined,
  type: "single" | "multiple",
): string[] {
  if (value === undefined) return [];
  if (type === "single") {
    return typeof value === "string" ? [value] : value.slice(0, 1);
  }
  return typeof value === "string" ? [value] : value;
}

/**
 * Accordion root component
 *
 * A compound component for building accessible accordion/collapsible UI.
 * Supports single or multiple expanded panels.
 *
 * @example
 * ```tsx
 * <Accordion type="multiple" defaultValue={["item-1"]}>
 *   <Accordion.Item value="item-1">
 *     <Accordion.Trigger>Section 1</Accordion.Trigger>
 *     <Accordion.Content>Content 1</Accordion.Content>
 *   </Accordion.Item>
 * </Accordion>
 * ```
 */
function AccordionRoot({
  type = "multiple",
  value: controlledValue,
  defaultValue,
  onValueChange,
  collapsible = true,
  disabled = false,
  className,
  children,
}: AccordionProps) {
  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = useState<string[]>(() =>
    normalizeValue(defaultValue, type),
  );

  // Determine if controlled
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled
    ? normalizeValue(controlledValue, type)
    : internalValue;

  // Toggle handler
  const onToggle = useCallback(
    (itemValue: string) => {
      let newValue: string[];

      if (type === "single") {
        // Single mode: toggle or replace
        if (currentValue.includes(itemValue)) {
          newValue = collapsible ? [] : currentValue;
        } else {
          newValue = [itemValue];
        }
      } else {
        // Multiple mode: toggle in array
        if (currentValue.includes(itemValue)) {
          newValue = currentValue.filter((v) => v !== itemValue);
        } else {
          newValue = [...currentValue, itemValue];
        }
      }

      // Update internal state if uncontrolled
      if (!isControlled) {
        setInternalValue(newValue);
      }

      // Call onChange callback
      if (onValueChange) {
        if (type === "single") {
          onValueChange(newValue[0] ?? "");
        } else {
          onValueChange(newValue);
        }
      }
    },
    [type, currentValue, collapsible, isControlled, onValueChange],
  );

  // Memoize context value
  const contextValue = useMemo(
    () => ({
      type,
      value: currentValue,
      onToggle,
      disabled,
      collapsible,
    }),
    [type, currentValue, onToggle, disabled, collapsible],
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={cx(styles.accordion, className)} data-accordion-root>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

/**
 * Accordion compound component with attached sub-components
 */
export const Accordion = Object.assign(AccordionRoot, {
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
});
